import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';
import { selectFormByName } from 'core/selectors/form';
import { selectSiteUrl } from 'core/selectors/app';
import {
  HYBRID_SEARCH_REDIRECT_TYPE_PRODUCT,
  HYBRID_SEARCH_REDIRECT_TYPE_MERCHANT_MULTIPLE,
  HYBRID_SEARCH_REDIRECT_TYPE_MERCHANT_SINGLE,
  HYBRID_SEARCH_REDIRECT_TYPE_MERCHANT_TOMBSTONE,
  HYBRID_SEARCH_REDIRECT_TYPE_NO_RESULTS,
  HYBRID_SEARCH_REDIRECT_TYPE_URL,
} from '../actions/types';

export const selectSearchFormKeywords = (state) => {
  const searchForm = selectFormByName(state, 'hybridSearchForm') || {};
  const { values = {} } = searchForm;
  return values.keywords || '';
};

export const selectSearchForm = state => state.hybridSearchForm;
export const selectSearchFormConfig = createSelector(
  selectSearchForm,
  hybridSearchForm => hybridSearchForm.config,
);

export const selectSearchFormIsLoading = createSelector(
  selectSearchForm,
  hybridSearchForm => hybridSearchForm.isLoading,
);

export const selectSearchFormIsLoaded = createSelector(
  selectSearchForm,
  hybridSearchForm => hybridSearchForm.isLoaded,
);

export const selectSearchFormSearchResult = createSelector(
  selectSearchForm,
  hybridSearchForm => hybridSearchForm.searchResult,
);

export const selectSearchFormDestinationMerchants = createSelector(
  selectSearchFormSearchResult,
  (searchFormSearchResult) => {
    if (isEmpty(searchFormSearchResult) || searchFormSearchResult.metadata.type !== 'Merchant') {
      return [];
    }
    // ignore instore merchants for now
    return searchFormSearchResult.response.filter(merchant => merchant.type !== 'instore');
  },
);

export const selectSearchFormRedirectType = createSelector(
  selectSearchFormSearchResult, selectSearchFormDestinationMerchants,
  (searchFormSearchResult, searchFormDestinationMerchants) => {
    let searchRedirectType = null;

    if (!isEmpty(searchFormSearchResult)) {
      switch (searchFormSearchResult.metadata.type) {
        case 'DestinationUrl':
          searchRedirectType = HYBRID_SEARCH_REDIRECT_TYPE_URL;
          break;
        case 'Merchant':
          if (searchFormDestinationMerchants.length > 1) {
            searchRedirectType = HYBRID_SEARCH_REDIRECT_TYPE_MERCHANT_MULTIPLE;
          } else if (searchFormDestinationMerchants.length === 1) {
            const merchant = searchFormDestinationMerchants[0];
            if (merchant.type === 'Suspended Merchant' || merchant.type === 'Deactivated Merchant') {
              searchRedirectType = HYBRID_SEARCH_REDIRECT_TYPE_MERCHANT_TOMBSTONE;
            } else {
              searchRedirectType = HYBRID_SEARCH_REDIRECT_TYPE_MERCHANT_SINGLE;
            }
          }
          break;
        case 'Product':
          searchRedirectType = HYBRID_SEARCH_REDIRECT_TYPE_PRODUCT;
          break;
        case 'NoResults':
          searchRedirectType = HYBRID_SEARCH_REDIRECT_TYPE_NO_RESULTS;
          break;
        default:
          searchRedirectType = null;
      }
    }

    return searchRedirectType;
  },
);

export const selectSearchFormRedirectUrl = createSelector(
  selectSearchFormRedirectType, selectSearchFormSearchResult, selectSearchFormKeywords, selectSiteUrl,
  (searchFormRedirectType, searchFormSearchResult, searchFormKeywords, siteUrl) => {
    let redirectUrl = null;
    let gmidsArr;

    if (!isEmpty(searchFormSearchResult)) {
      switch (searchFormRedirectType) {
        case HYBRID_SEARCH_REDIRECT_TYPE_URL:
          redirectUrl = searchFormSearchResult.response.value;
          break;
        case HYBRID_SEARCH_REDIRECT_TYPE_MERCHANT_SINGLE:
        case HYBRID_SEARCH_REDIRECT_TYPE_MERCHANT_MULTIPLE:
        case HYBRID_SEARCH_REDIRECT_TYPE_MERCHANT_TOMBSTONE:
          gmidsArr = searchFormSearchResult.response.reduce((arr, merchant) => {
            if (merchant.type !== 'instore') { // ignore instore merchants for now
              arr.push(merchant.id);
            }
            return arr;
          }, []);
          redirectUrl =
            `${siteUrl}/me____.htm?` +
            `keywords=${encodeURIComponent(searchFormKeywords)}&gmid=${gmidsArr.join()}`;
          break;
        case HYBRID_SEARCH_REDIRECT_TYPE_PRODUCT:
          redirectUrl =
            `${siteUrl}/hsr____.htm?` +
            `keywords=${encodeURIComponent(searchFormKeywords)}`;
          break;
        case HYBRID_SEARCH_REDIRECT_TYPE_NO_RESULTS:
          redirectUrl =
            `${siteUrl}/nr____.htm?` +
            `keywords=${encodeURIComponent(searchFormKeywords)}`;
          break;
        default:
          redirectUrl = null;
      }
    }

    return redirectUrl;
  },
);
